import { Link } from 'react-router';
import type { OpsOrganization } from '@gatecheckpro/contracts';
import {
  EmptyState,
  LoadingRegion,
  Status,
  presentation,
  VERIFICATION_STATES,
} from '@gatecheckpro/design-system';
import { useResource } from '../lib/useResource.ts';
import { FailureNotice } from '../lib/refusal.tsx';

/**
 * The onboarding queue.
 *
 * Ordered oldest first, because the organizer who has been waiting longest is
 * the one whose event is closest to needing a decision.
 */
export function OnboardingQueuePage() {
  const { resource } = useResource<{ organizations: OpsOrganization[] }>(
    '/api/v1/ops/organizations?states=SUBMITTED,IN_REVIEW',
  );

  if (resource.status === 'loading') return <LoadingRegion label="Loading the queue" />;
  if (resource.status === 'failed') return <FailureNotice error={resource.error} />;

  return (
    <div className="gc-stack">
      <div className="gc-page-header">
        <span className="gc-eyebrow">Organizer onboarding</span>
        <h1>Waiting for a decision</h1>
        <p className="gc-lede">
          Whether an organizer may take money from the public. Every decision here is recorded with
          who made it and why.
        </p>
      </div>

      {resource.data.organizations.length === 0 ? (
        <EmptyState title="Nothing is waiting">
          No organization has submitted for verification. Submissions appear here as soon as an
          organizer sends one.
        </EmptyState>
      ) : (
        <div className="gc-table-scroll">
          <table className="gc-table">
            <thead>
              <tr>
                <th scope="col">Organization</th>
                <th scope="col">State</th>
                <th scope="col">Submitted</th>
                <th scope="col">Legal name</th>
                <th scope="col">
                  <span className="gc-visually-hidden">Review</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {resource.data.organizations.map((organization) => {
                const state = presentation(VERIFICATION_STATES, organization.verification.state);
                return (
                  <tr key={organization.organizationId}>
                    <td>
                      <div className="gc-stack gc-stack--tight">
                        <strong>{organization.name}</strong>
                        <span className="gc-muted">{organization.slug}</span>
                      </div>
                    </td>
                    <td>
                      <Status
                        tone={state.tone}
                        glyph={state.glyph}
                        label={state.label}
                        title={state.meaning}
                      />
                    </td>
                    <td className="gc-mono">
                      {organization.verification.submittedAt === null
                        ? '—'
                        : new Date(organization.verification.submittedAt).toLocaleString()}
                    </td>
                    <td>{organization.evidence.legalName ?? '—'}</td>
                    <td>
                      <Link to={`/onboarding/${organization.organizationId}`} className="gc-button">
                        Review
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
